<template>
  <div>
    <el-card v-loading="loading">
      <template #header>
        <div class="card-header">
          <span>调拨数量</span>
          <el-col :span="6">
            <el-date-picker
              v-model="datePicker"
              type="monthrange"
              placeholder="请选择"
              style="width: 100%"
              @change="datePickerChange"
              format="YYYY-MM"
              value-format="YYYY-MM"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              :clearable="false"
            />
          </el-col>
        </div>
      </template>
      <div class="card-header">
        <div ref="chart" class="chart"></div>
        <div style="width: 30%">
          <span>物料排名</span>
          <el-table
            :data="tableData"
            :show-header="false"
            size="large"
            style="margin-top: 10px"
            :cell-style="{ 'text-align': 'center' }"
          >
            <el-table-column type="index">
              <template #default="{ $index }">
                <span class="index">{{ $index + 1 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" />
            <el-table-column prop="quantity" />
          </el-table>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import * as echarts from "echarts/core";
import { GridComponent, GraphicComponent } from "echarts/components";
import { BarChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import {
  getCurrentInstance,
  reactive,
  watchEffect,
  onUnmounted,
  nextTick,
  onMounted,
  ref,
} from "vue";
import { allocateQuantityChart, queryMaterialRank } from "@/api/statistics";

echarts.use([GridComponent, BarChart, CanvasRenderer, GraphicComponent]);

const option = reactive({
  xAxis: {
    type: "category",
    data: [],
  },
  yAxis: {
    type: "value",
    name: "调拨数量趋势",
    minInterval: 1,
  },
  series: [
    {
      data: [],
      type: "bar",
      label: {
        show: true,
        position: "top",
      },
      barMaxWidth: 30,
    },
  ],
  graphic: {
    elements: [
      {
        type: "text",
        left: "center",
        top: "middle",
        silent: true,
        invisible: false,
        style: {
          fill: "black",
          fontWeight: "bold",
          text: "暂无数据",
          fontSize: "26px",
        },
        z: 200,
      },
    ],
  },
});

const { proxy } = getCurrentInstance();

const loading = ref(false);

const tableData = ref([]);

const charts = {
  chart: null,
};

const trendList = ref([]);

const initChart = () => {
  if (charts.chart) charts.chart.dispose();
  const chartDom = proxy.$refs.chart;
  charts.chart = echarts.init(chartDom);
  charts.chart.setOption(option);

  const resizeHandler = () => {
    charts.chart.resize();
  };

  window.addEventListener("resize", resizeHandler);

  watchEffect(() => {
    onUnmounted(() => {
      window.removeEventListener("resize", resizeHandler);
      charts.chart.dispose();
    });
  });
};

// 日期范围选择器
const datePicker = ref("");

// 时间范围选择器变化
const datePickerChange = (val) => {
  searchData.startTime = val?.[0] ?? null;
  searchData.endTime = val?.[1] ?? null;
  getChart();
  getRankList();
};

// 搜索表单
const searchData = reactive({
  startTime: null,
  endTime: null,
});

// 获取趋势数据
const getChart = () => {
  if (!searchData.endTime || !searchData.startTime) return;
  loading.value = true;
  if (charts.chart) charts.chart.clear();
  option.series[0].data = [];
  option.xAxis.data = [];
  const arr = [];
  allocateQuantityChart({ ...searchData })
    .then((res) => {
      trendList.value = res.data || [];
      trendList.value.forEach((item) => {
        arr.push(item.date.slice(0, 7));
        option.series[0].data.push(item.quantity);
      });
      option.xAxis.data = arr;
    })
    .finally(() => {
      option.graphic.elements[0].invisible = arr.length == 0 ? false : true;
      charts.chart.setOption(option);
      charts.chart.resize();
      loading.value = false;
    });
};

// 获取排名数据
const getRankList = () => {
  queryMaterialRank({
    ...searchData,
  }).then((res) => {
    tableData.value = res.data;
  });
};

const dataFormat = (myDate) => {
  let Y = myDate.getFullYear(); //获取当前完整年份
  let M = myDate.getMonth() + 1; //获取当前月份
  let D = myDate.getDate(); //获取当前日1-31
  let H = myDate.getHours(); //获取当前小时
  let i = myDate.getMinutes(); //获取当前分钟
  let s = myDate.getSeconds(); //获取当前秒数
  // 月份不足10补0
  if (M < 10) {
    M = "0" + M;
  }
  // 日不足10补0
  if (D < 10) {
    D = "0" + D;
  }
  // 小时不足10补0
  if (H < 10) {
    H = "0" + H;
  }
  // 分钟不足10补0
  if (i < 10) {
    i = "0" + i;
  }
  // 秒数不足10补0
  if (s < 10) {
    s = "0" + s;
  }
  // 拼接日期分隔符根据自己的需要来修改
  let nowDate = Y + "-" + M + "-" + D + " " + H + ":" + i + ":" + s;
  return nowDate;
};

const getLastWeekRange = () => {
  // 获取当前日期
  let now = new Date();

  // // 获取当前日期的时间戳
  // let currentTimestamp = now.getTime();

  // 计算一个月前的时间戳
  let oneMonthAgo = new Date(now);
  oneMonthAgo.setMonth(now.getMonth() - 1);

  // 格式化日期
  function formatDate(date) {
    let year = date.getFullYear();
    let month = (date.getMonth() + 1).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");
    // let hours = date.getHours().toString().padStart(2, "0");
    // let minutes = date.getMinutes().toString().padStart(2, "0");
    // let seconds = date.getSeconds().toString().padStart(2, "0");
    return `${year}-${month}`;
  }

  // 获取一月前和当前时间的格式化字符串
  let oneMonthAgoFormatted = formatDate(oneMonthAgo);
  let nowFormatted = formatDate(now);

  datePicker.value = [oneMonthAgoFormatted, nowFormatted];
  searchData.startTime = datePicker.value?.[0] ?? null;
  searchData.endTime = datePicker.value?.[1] ?? null;
};

onMounted(() => {
  getLastWeekRange();
  getChart();
  getRankList();
  nextTick(() => {
    initChart();
  });
});
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
}

.chart {
  height: 500px;
  width: 100%;
}

.index {
  display: block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #409eff;
  line-height: 20px;
  text-align: center;
  color: #fff;
}
</style>
